// mindmetal-app/web/src/components/employee/ResponseViewer.tsx
import { useState } from 'react'
import { Copy, Check, ChevronDown, ChevronRight } from 'lucide-react'

interface ResponseViewerProps {
  status: number
  statusText: string
  headers: Record<string, string>
  body: unknown
  responseTime: number
}

export function ResponseViewer({
  status,
  statusText,
  headers,
  body,
  responseTime,
}: ResponseViewerProps) {
  const [showHeaders, setShowHeaders] = useState(false)
  const [copied, setCopied] = useState(false)

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'text-emerald-600 bg-emerald-50'
    if (status >= 300 && status < 400) return 'text-blue-600 bg-blue-50'
    if (status >= 400 && status < 500) return 'text-amber-600 bg-amber-50'
    return 'text-red-600 bg-red-50'
  }

  const formatJson = (data: unknown): string => {
    try {
      return JSON.stringify(data, null, 2)
    } catch {
      return String(data)
    }
  }

  const highlightJson = (json: string): string => {
    return json
      .replace(/"([^"]+)":/g, '<span class="text-copper-600">"$1"</span>:')
      .replace(/: "([^"]*?)"/g, ': <span class="text-sage-600">"$1"</span>')
      .replace(/: (\d+)/g, ': <span class="text-sky-600">$1</span>')
      .replace(/: (true|false)/g, ': <span class="text-amber-600">$1</span>')
      .replace(/: (null)/g, ': <span class="text-warm-400">$1</span>')
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formatJson(body))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const formattedBody = formatJson(body)

  return (
    <div className="border border-warm-200 rounded-xl overflow-hidden">
      {/* Status Bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-warm-50 border-b border-warm-200">
        <div className="flex items-center gap-4">
          <span className={`px-3 py-1 rounded-lg font-mono font-semibold ${getStatusColor(status)}`}>
            {status} {statusText}
          </span>
          <span className="text-sm text-warm-500">
            {responseTime}ms
          </span>
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-warm-600 hover:text-midnight-800 hover:bg-warm-100 rounded-lg transition-colors"
        >
          {copied ? (
            <>
              <Check className="w-4 h-4 text-emerald-500" />
              Copied
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copy
            </>
          )}
        </button>
      </div>

      {/* Headers Accordion */}
      <button
        onClick={() => setShowHeaders(!showHeaders)}
        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-warm-600 hover:bg-warm-50 border-b border-warm-200"
      >
        {showHeaders ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
        Headers ({Object.keys(headers).length})
      </button>

      {showHeaders && (
        <div className="px-4 py-2 bg-warm-50 border-b border-warm-200 text-sm font-mono">
          {Object.entries(headers).map(([key, value]) => (
            <div key={key} className="py-0.5">
              <span className="text-copper-600">{key}:</span>{' '}
              <span className="text-warm-700">{value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Body */}
      <div className="relative">
        <pre
          className="p-4 bg-midnight-900 text-warm-100 overflow-x-auto text-sm font-mono max-h-96"
          dangerouslySetInnerHTML={{ __html: highlightJson(formattedBody) }}
        />
      </div>
    </div>
  )
}
